import * as SunCalc from 'suncalc';

// More accurate lunar phase calculation using astronomical formulas
// Based on Jean Meeus's "Astronomical Algorithms"

export type LunarPhase = 
  | 'new_moon' 
  | 'waxing_crescent' 
  | 'first_quarter' 
  | 'waxing_gibbous' 
  | 'full_moon' 
  | 'waning_gibbous' 
  | 'last_quarter' 
  | 'waning_crescent';

export interface LunarPhaseData {
  phase: LunarPhase;
  illumination: number;
  icon: string;
  name: string;
  age: number; // Age of moon in days
  cycleDay: number; // Which day of the 29.5-day lunar cycle
  cycleDescription: string; // Human-readable description like "Day 15 of lunar cycle"
  isWaxing: boolean; // Whether moon is waxing or waning
  waxingDescription: string; // Human-readable waxing/waning description
}

export const LUNAR_PHASE_ICONS: Record<LunarPhase, string> = {
  new_moon: 'new-moon',
  waxing_crescent: 'waxing-crescent',
  first_quarter: 'first-quarter',
  waxing_gibbous: 'waxing-gibbous',
  full_moon: 'full-moon',
  waning_gibbous: 'waning-gibbous',
  last_quarter: 'last-quarter',
  waning_crescent: 'waning-crescent',
};

export const LUNAR_PHASE_NAMES: Record<LunarPhase, string> = {
  new_moon: 'New Moon',
  waxing_crescent: 'Waxing Crescent',
  first_quarter: 'First Quarter',
  waxing_gibbous: 'Waxing Gibbous',
  full_moon: 'Full Moon',
  waning_gibbous: 'Waning Gibbous',
  last_quarter: 'Last Quarter',
  waning_crescent: 'Waning Crescent',
};

// Calculate Julian Day Number from date
function getJulianDay(date: Date): number {
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // JavaScript months are 0-indexed
  const day = date.getDate();
  const hour = date.getHours();
  const minute = date.getMinutes();
  const second = date.getSeconds();
  
  let a = Math.floor((14 - month) / 12);
  let y = year - a;
  let m = month + 12 * a - 3;
  
  let jd = day + Math.floor((153 * m + 2) / 5) + 365 * y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) - 32045;
  
  // Add time fraction
  jd += (hour - 12) / 24 + minute / 1440 + second / 86400;
  
  return jd;
}

// More accurate moon age calculation using 2025 astronomical reference
function getMoonAge(julianDay: number): number {
  // Using verified new moon reference: January 29, 2025, 12:36 UTC (NASA data)
  // JD for this new moon is 2460706.025000000
  const referenceNewMoon = 2460706.025;
  const synodicMonth = 29.530588861; // Most precise synodic month length from NASA
  
  const daysSinceReference = julianDay - referenceNewMoon;
  
  // Calculate moon age in current cycle
  const moonAge = daysSinceReference % synodicMonth;
  
  // Ensure moonAge is positive and within synodic month
  const normalizedMoonAge = ((moonAge % synodicMonth) + synodicMonth) % synodicMonth;
  
  return normalizedMoonAge;
}

// Calculate illuminated fraction using more accurate formula
function getMoonIllumination(moonAge: number): number {
  const synodicMonth = 29.530588861;
  const phase = (moonAge / synodicMonth) * 2 * Math.PI;
  
  // More accurate illumination calculation accounting for phase angle
  const illumination = (1 - Math.cos(phase)) / 2;
  return Math.max(0, Math.min(1, illumination));
}

export function calculateLunarPhase(date: Date): LunarPhaseData {
  // Use SunCalc library for accurate lunar calculations
  const moonIllumination = SunCalc.getMoonIllumination(date);
  
  // Get phase angle (0 to 1, where 0/1 = new moon, 0.5 = full moon)
  const phaseValue = moonIllumination.phase;
  
  // Calculate illumination percentage
  const illuminationPercent = Math.round(moonIllumination.fraction * 100);
  
  // Calculate moon age in days (0-29.53)
  const synodicMonth = 29.530588861;
  const moonAge = phaseValue * synodicMonth;
  
  // Calculate cycle day (1-30)
  const cycleDay = Math.max(1, Math.min(30, Math.round(moonAge) + 1));
  const cycleDescription = `Day ${cycleDay} of lunar cycle`;
  
  // Determine phase based on phase value
  let phase: LunarPhase;
  let isWaxing: boolean;
  
  // Phase boundaries based on phase value (0-1)
  if (phaseValue < 0.0625 || phaseValue >= 0.9375) {
    // New Moon: ±3.125% around 0/1
    phase = 'new_moon';
    isWaxing = true;
  } else if (phaseValue < 0.1875) {
    // Waxing Crescent: 6.25% to 18.75%
    phase = 'waxing_crescent';
    isWaxing = true;
  } else if (phaseValue < 0.3125) {
    // First Quarter: 18.75% to 31.25%
    phase = 'first_quarter';
    isWaxing = true;
  } else if (phaseValue < 0.4375) {
    // Waxing Gibbous: 31.25% to 43.75%
    phase = 'waxing_gibbous';
    isWaxing = true;
  } else if (phaseValue < 0.5625) {
    // Full Moon: 43.75% to 56.25%
    phase = 'full_moon';
    isWaxing = false;
  } else if (phaseValue < 0.6875) {
    // Waning Gibbous: 56.25% to 68.75%
    phase = 'waning_gibbous';
    isWaxing = false;
  } else if (phaseValue < 0.8125) {
    // Last Quarter: 68.75% to 81.25%
    phase = 'last_quarter';
    isWaxing = false;
  } else {
    // Waning Crescent: 81.25% to 93.75%
    phase = 'waning_crescent';
    isWaxing = false;
  }

  const waxingDescription = isWaxing ? 'Waxing (Growing)' : 'Waning (Shrinking)';

  return {
    phase,
    illumination: illuminationPercent,
    icon: LUNAR_PHASE_ICONS[phase],
    name: LUNAR_PHASE_NAMES[phase],
    age: moonAge,
    cycleDay,
    cycleDescription,
    isWaxing,
    waxingDescription,
  };
}

export function calculateCycleDay(startDate: Date, currentDate: Date): number {
  const diffTime = currentDate.getTime() - startDate.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(1, diffDays + 1);
}

export function calculateExpectedPeriodDates(startDate: Date, cycleLength: number = 28): Date[] {
  const dates: Date[] = [];
  const currentDate = new Date();
  const endDate = new Date(currentDate.getTime() + (6 * 30 * 24 * 60 * 60 * 1000)); // 6 months ahead
  
  let nextPeriod = new Date(startDate);
  
  while (nextPeriod <= endDate) {
    dates.push(new Date(nextPeriod));
    nextPeriod.setDate(nextPeriod.getDate() + cycleLength);
  }
  
  return dates;
}

export type CyclePhase = 'menstruation' | 'follicular' | 'ovulation' | 'luteal' | 'perimenopause' | 'menopause';

export interface CyclePhaseInfo {
  phase: CyclePhase;
  name: string;
  color: string;
  description: string;
}

export function getCyclePhase(cycleDay: number, cycleLength: number = 28, isPerimenopausal: boolean = false, isMenopausal: boolean = false, periodLength: number = 5): CyclePhaseInfo {
  // Menopause - no cycles
  if (isMenopausal) {
    return {
      phase: 'menopause',
      name: 'Menopause',
      color: 'text-amber-600',
      description: 'Post-menopausal phase'
    };
  }
  
  // Perimenopause - irregular cycles
  if (isPerimenopausal) {
    return {
      phase: 'perimenopause',
      name: 'Perimenopause',
      color: 'text-orange-600',
      description: 'Transitional phase with irregular cycles'
    };
  }
  
  // Normalize cycle day to be within 1-cycleLength range
  const normalizedCycleDay = ((cycleDay - 1) % cycleLength) + 1;
  
  // 🔴 Menstruation: Days 1 to user-defined period length (default 5)
  if (normalizedCycleDay >= 1 && normalizedCycleDay <= periodLength) {
    return {
      phase: 'menstruation',
      name: 'Menstruation',
      color: 'text-red-500',
      description: `Period days (1-${periodLength})`
    };
  }
  
  // Calculate ovulation dynamically based on cycle length
  // Ovulation typically occurs 12-16 days before next period
  // For flexibility, use midpoint ± 1 day as starting estimate
  const lutealPhaseLength = Math.round(cycleLength * 0.43); // ~12-14 days typically
  const ovulationStart = cycleLength - lutealPhaseLength;
  const ovulationEnd = ovulationStart + 1; // 2-day ovulation window
  
  // 💗 Ovulation: Midpoint of cycle ± 1 day (dynamically calculated)
  if (normalizedCycleDay >= ovulationStart && normalizedCycleDay <= ovulationEnd) {
    return {
      phase: 'ovulation',
      name: 'Ovulation',
      color: 'text-pink-500',
      description: `Most fertile days (${ovulationStart}-${ovulationEnd})`
    };
  }
  
  // 💜 Luteal: From ovulation to start of next period (dynamically calculated)
  if (normalizedCycleDay > ovulationEnd) {
    return {
      phase: 'luteal',
      name: 'Luteal Phase',
      color: 'text-purple-500',
      description: `Pre-menstrual phase (${ovulationEnd + 1}-${cycleLength})`
    };
  }
  
  // 🩵 Follicular: From end of menstruation to ovulation (dynamically calculated)
  return {
    phase: 'follicular',
    name: 'Follicular Phase',
    color: 'text-cyan-400',
    description: `Preparing for ovulation (${periodLength + 1}-${ovulationStart - 1})`
  };
}